import { Directive, InjectionToken, ElementRef, NgZone, inject, output } from '@angular/core';
import { ResizeObserver } from '@juggle/resize-observer';
import type {
  OnDestroy,
  OnInit
} from '@angular/core';

export enum ContainerSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

export interface ContainerSizeValue {
  small: number;
  medium: number;
  large: number;
}

export const CONTAINER_SIZE_VALUE = new InjectionToken<ContainerSizeValue>('containerSizeValue');

@Directive({
  selector: '[tdWbContainerSize]',
  exportAs: 'tdWbContainerSize',
  host: {
    '[class.td-wb-container-size--small]': 'small',
    '[class.td-wb-container-size--medium]': 'medium',
    '[class.td-wb-container-size--large]': 'large'
  }
})
export class ContainerSizeDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef>(ElementRef);
  private readonly ngZone = inject<NgZone>(NgZone);

  small: boolean = false;
  medium: boolean = false;
  large: boolean = false;
  readonly sizeChange = output<ContainerSize>();

  private resizeObserver: ResizeObserver;

  // Matching breakpoints - 30 padding for small and medium screens
  private readonly containerSizeValue: ContainerSizeValue = {
    small: 767 - 30,
    medium: 1023 - 30,
    large: 1365
  };

  constructor() {
    const configuration = inject<ContainerSizeValue>(CONTAINER_SIZE_VALUE, { optional: true });

    if (configuration !== null && configuration !== undefined) {
      this.containerSizeValue = configuration;
    }
  }

  ngOnInit(): void {
    this.setResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver !== undefined) {
      this.resizeObserver.unobserve(this.elementRef.nativeElement);
    }
  }

  private setResizeObserver(): void {
    if (this.resizeObserver === undefined) {
      this.resizeObserver = new ResizeObserver((entries: Iterable<ResizeObserverEntry>): void => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            if (entry.contentBoxSize[0]) {
              this.setSizeClass(entry.contentBoxSize[0].inlineSize);
              // } else {
              // TODO was this needed?
              //   this.setSizeClass(entry.contentBoxSize.inlineSize);
            }
          } else {
            this.setSizeClass(entry.contentRect.width);
          }
        }
      });
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }
  }

  private setSizeClass(width: number): void {
    this.ngZone.run((): void => {
      this.small = width <= this.containerSizeValue.small;
      this.medium = width > this.containerSizeValue.small && width <= this.containerSizeValue.medium;
      this.large = width > this.containerSizeValue.medium && width <= this.containerSizeValue.large;

      this.sizeChange.emit(this.getCurrentSize());
    });
  }

  private getCurrentSize(): ContainerSize {
    if (this.small) {
      return ContainerSize.SM;
    } else if (this.medium) {
      return ContainerSize.MD;
    } else if (this.large) {
      return ContainerSize.LG;
    }
    return undefined;
  }
}
